import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import {
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  PhoneCall,
  RefreshCw,
} from "lucide-react";
import {
  getAllContactHistories,
  getContactHistoryById,
  getContactHistoriesByCustomer,
} from "../../../api/crm/contact-history/info";
import ContactHistoryDetailModal from "../../../modules/crm/contact-history/ContactHistoryDetailModal";
import { showErrorToast } from "../../../components/ConfirmDialog";
import {
  MobilePageShell,
  MobilePageHeader,
} from "../MobileNavPrimitives";

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function getFollowupMeta(dateStr) {
  if (!dateStr) return null;
  const target = startOfDay(dateStr);
  const today = startOfDay(new Date());
  const diff = Math.round((target - today) / 86400000);
  if (diff < 0) {
    return {
      short: `Quá hạn ${Math.abs(diff)}n`,
      chip: "bg-rose-50 text-rose-700 border-rose-100",
      rank: 0,
    };
  }
  if (diff === 0) {
    return {
      short: "Hôm nay",
      chip: "bg-amber-50 text-amber-800 border-amber-100",
      rank: 1,
    };
  }
  if (diff === 1) {
    return {
      short: "Ngày mai",
      chip: "bg-orange-50 text-orange-700 border-orange-100",
      rank: 2,
    };
  }
  return {
    short: format(target, "dd/MM", { locale: vi }),
    chip: "bg-slate-50 text-slate-600 border-slate-100",
    rank: 3,
  };
}

export default function MobileCrmFollowupsScreen() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [items, setItems] = useState([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailContact, setDetailContact] = useState(null);
  const [relatedLogs, setRelatedLogs] = useState([]);
  const [filter, setFilter] = useState("all"); // all | overdue | today

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const data = await getAllContactHistories({
        has_followup: "1",
        page: 1,
        pageSize: 80,
      });
      const list = (Array.isArray(data?.items) ? data.items : [])
        .map((item) => ({
          ...item,
          _meta: getFollowupMeta(item.next_contact_date),
        }))
        .filter((item) => item._meta)
        .sort((a, b) => {
          const rd = (a._meta.rank ?? 9) - (b._meta.rank ?? 9);
          if (rd !== 0) return rd;
          const at = a.next_contact_date
            ? new Date(a.next_contact_date).getTime()
            : 0;
          const bt = b.next_contact_date
            ? new Date(b.next_contact_date).getTime()
            : 0;
          return at - bt;
        });
      setItems(list);
    } catch (e) {
      console.error(e);
      showErrorToast("Không tải được lịch follow-up");
      setItems([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  const counts = useMemo(
    () => ({
      overdue: items.filter((x) => x._meta.rank === 0).length,
      today: items.filter((x) => x._meta.rank === 1).length,
      all: items.length,
    }),
    [items]
  );

  const filtered = useMemo(() => {
    if (filter === "overdue") return items.filter((x) => x._meta.rank === 0);
    if (filter === "today") return items.filter((x) => x._meta.rank === 1);
    return items;
  }, [items, filter]);

  const openDetail = async (id) => {
    try {
      const data = await getContactHistoryById(id);
      let related = [];
      if (data?.customer_id) {
        try {
          const rel = await getContactHistoriesByCustomer(data.customer_id);
          related = Array.isArray(rel?.items)
            ? rel.items
            : Array.isArray(rel)
              ? rel
              : [];
        } catch {
          related = [];
        }
      }
      setDetailContact(data);
      setRelatedLogs(related);
      setDetailOpen(true);
    } catch (e) {
      console.error(e);
      showErrorToast("Không mở được chi tiết liên hệ");
    }
  };

  return (
    <MobilePageShell>
      <MobilePageHeader
        title="Ưu tiên chăm sóc"
        subtitle="Follow-up quá hạn & sắp tới"
        tone="amber"
        trailing={
          <button
            type="button"
            onClick={() => load(true)}
            disabled={refreshing || loading}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-amber-100 bg-amber-50 text-amber-700 active:bg-amber-100 disabled:opacity-50"
            aria-label="Làm mới"
          >
            <RefreshCw
              size={16}
              className={refreshing ? "animate-spin" : undefined}
            />
          </button>
        }
      >
        <button
          type="button"
          onClick={() => navigate("/m/crm")}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-[12px] font-medium text-gray-700 active:bg-gray-50"
        >
          <ArrowLeft size={14} />
          Về CRM
        </button>
      </MobilePageHeader>

      <div className="px-5 pt-4 pb-4 space-y-4">
        <div className="flex flex-wrap gap-2">
          {[
            { key: "all", label: `Tất cả (${counts.all})` },
            { key: "overdue", label: `Quá hạn (${counts.overdue})` },
            { key: "today", label: `Hôm nay (${counts.today})` },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setFilter(tab.key)}
              className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold ${
                filter === tab.key
                  ? "border-amber-200 bg-amber-50 text-amber-800"
                  : "border-gray-200 bg-white text-gray-600"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="overflow-hidden rounded-2xl border border-amber-100/80 bg-white shadow-sm">
          {loading ? (
            <div className="space-y-3 p-4">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-16 animate-pulse rounded-xl bg-gray-100"
                />
              ))}
            </div>
          ) : filtered.length > 0 ? (
            <ul className="divide-y divide-amber-50">
              {filtered.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => openDetail(item.id)}
                    className="flex w-full items-start gap-3 px-4 py-3.5 text-left active:bg-amber-50/40"
                  >
                    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
                      <PhoneCall size={16} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-800">
                        {item.customer_name || "Khách hàng"}
                      </p>
                      <p className="mt-0.5 truncate text-[11px] text-gray-400">
                        {[item.branch_name, item.method, item.employee_name]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                      <span
                        className={`mt-1.5 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${item._meta.chip}`}
                      >
                        {item._meta.short}
                      </span>
                    </div>
                    <ChevronRight
                      size={14}
                      className="mt-2 shrink-0 text-gray-300"
                    />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex flex-col items-center px-6 py-12 text-center">
              <CheckCircle2 className="mb-2 text-emerald-500" size={28} />
              <p className="text-[13px] text-gray-400">
                Không có follow-up trong bộ lọc này
              </p>
            </div>
          )}
        </div>
      </div>

      <ContactHistoryDetailModal
        isOpen={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setDetailContact(null);
          setRelatedLogs([]);
        }}
        contact={detailContact}
        relatedLogs={relatedLogs}
      />
    </MobilePageShell>
  );
}
